import React, { useContext, useState } from 'react'
import { toast } from 'react-toastify'
import Swal from "sweetalert2";
import axios from "axios";
import { api_url } from "../../data";
import { ThreeDots } from "react-loader-spinner";
import { useGlobalContext } from '../../context/stateContext';
import user_image from "../../assets/avatar.jpg"
import { useAuth } from '../../context/Auth';

const PersonalSettings = () => {
    const {requestWithAuth} = useAuth();
    const {user} = useGlobalContext()
    const [submitting, setSubmitting] = useState(false)
    const [newPassword, setNewPassword] = useState(null)
    const [newPasswordConfirm, setNewPasswordConfirm] = useState(null)

    // SUBMIT FORM
    const resetPassword = async (e) => {
        e.preventDefault()
        if (newPassword !== null && newPassword === newPasswordConfirm) {
            try {
                setSubmitting(true)
                const body = JSON.stringify({
                    "new_password": newPassword
                })
                const response = await requestWithAuth(`/accounts/user/password/reset/`, "PUT", body,{
                    "Content-Type":"application/json"
                });
                Swal.fire({
                    icon: "success",
                    title: "Success",
                    text: "Password Changed Successfully",
                }).then(() => {
                    window.location.reload();
                });
            } catch (error) {
                toast.error("An error occurred.")
            }finally{
                setSubmitting(false)
            }
            
        } else {
            toast.error("Passwords do not match")
        }
    };

    return (
        <div className="max-w-4xl mx-auto space-y-8">
            {/* Header Section */}
            <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Personal Profile</h2>
                <p className="text-gray-600">
                    Manage your personal information and account settings
                </p>
            </div>

            {/* Profile Information Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Profile Information</h3>
                
                {/* Profile Picture Section */}
                <div className="flex items-start space-x-6 mb-8">
                    {/* Profile Picture */}
                    <div className="flex-shrink-0">
                        <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-gray-200 shadow-sm">
                            <img 
                                src={user?.profile_picture ? user?.profile_picture : user_image} 
                                alt="Profile" 
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </div>

                    {/* Profile Picture Info */}
                    <div className="flex-1">
                        <h4 className="text-lg font-medium text-gray-900 mb-2">Profile Picture</h4>
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                            <div className="flex items-start">
                                <svg className="w-5 h-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                <p className="text-sm text-blue-700">
                                    To change your profile picture, please contact the administrator
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Personal Details Form */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">
                            First Name
                        </label>
                        <input 
                            type="text" 
                            disabled 
                            value={user?.first_name || ''} 
                            name="first_name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                        />
                    </div>
                    
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">
                            Last Name
                        </label>
                        <input 
                            type="text" 
                            disabled 
                            value={user?.last_name || ''} 
                            name="last_name"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                        />
                    </div>
                    
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">
                            Username
                        </label>
                        <input 
                            type="text" 
                            disabled 
                            value={user?.username || ''} 
                            name="username"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                        />
                    </div>
                </div>

                <div className="mt-6 p-4 bg-gray-50 rounded-md">
                    <p className="text-sm text-gray-600 flex items-center">
                        <svg className="w-4 h-4 text-gray-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                        </svg>
                        These fields are read-only. Contact your administrator to make changes.
                    </p>
                </div>
            </div>

            {/* Change Password Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Change Password</h3>
                    <p className="text-gray-600">
                        Update your password to keep your account secure
                    </p>
                </div>

                <form onSubmit={e => resetPassword(e)} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-gray-700">
                                New Password
                            </label>
                            <div className="relative">
                                <input 
                                    type="password" 
                                    onChange={e => setNewPassword(e.target.value)} 
                                    name="new_password"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                    placeholder="Enter new password"
                                    required
                                />
                                <svg className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                            </div>
                        </div>
                        
                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-gray-700">
                                Confirm New Password
                            </label>
                            <div className="relative">
                                <input 
                                    type="password" 
                                    onChange={e => setNewPasswordConfirm(e.target.value)} 
                                    name="new_password_confirm"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                    placeholder="Confirm new password"
                                    required
                                />
                                <svg className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>

                    {/* Password Requirements */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                        <div className="flex items-start">
                            <svg className="w-5 h-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <div>
                                <p className="text-sm text-yellow-800 font-medium">Password Requirements:</p>
                                <ul className="text-sm text-yellow-700 mt-1 space-y-1">
                                    <li>• At least 8 characters long</li>
                                    <li>• Include uppercase and lowercase letters</li>
                                    <li>• Include at least one number</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end">
                        {submitting ? (
                            <div className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 cursor-not-allowed">
                                <ThreeDots
                                    height="20"
                                    width="20"
                                    radius="9"
                                    color="#ffffff"
                                    ariaLabel="three-dots-loading"
                                    wrapperStyle={{}}
                                    wrapperClassName=""
                                    visible={true}
                                />
                                <span className="ml-2">Changing Password...</span>
                            </div>
                        ) : (
                            <button
                                type="submit"
                                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 shadow-sm"
                            >
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                Change Password
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    )
}

export default PersonalSettings